import { useCallback, useEffect, useState } from 'react';
import { money, expectedLabel } from '../lib/format';
import { toReceipt } from '../lib/receipt';
import {
  PROOF_LABEL,
  SIGNED_BY_LABEL,
  isTerminal,
  labelOf,
  type OrderStatus,
  type SpecialItemDto,
} from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import {
  STATUS_META,
  type DriverRow,
  type OrderDetail as Detail,
  type ZoneRow,
} from '../lib/types';
import { HandlingEditor } from './HandlingEditor';
import { OrderActions } from './OrderActions';
import { OrderTimeline } from './OrderTimeline';
import { ProofGallery } from './ProofGallery';
import { PayoutEditor } from './PayoutEditor';
import { PrintDialog } from './PrintDialog';
import { ChangePhoneDialog } from './ChangePhoneDialog';
import { Banknote, Printer, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 订单详情抽屉。
 *
 * 用抽屉不用整页跳转:调度员的工作是「扫列表 → 看某一单 → 回列表 → 看下一单」,
 * 整页跳转每次回来都要重新定位、重新加载、丢掉筛选和滚动位置。
 * 成熟的调度台(Onfleet / Shipday)都是这个形态。
 */
interface NotificationRow {
  channel: string;
  provider: string;
  status: string;
  skipReason: string | null;
  createdAt: string;
}

/** 通道的中文 —— 界面上不能出现 PUSH / SMS 这种码值 */
const CHANNEL_LABEL: Record<string, string> = {
  PUSH: '推送',
  SMS: '短信',
  EMAIL: '邮件',
  WEBHOOK: '回调',
};

export function OrderDetail({
  id,
  timezone,
  drivers,
  zones,
  permissions,
  features,
  onClose,
  onRepeat,
  onChanged,
}: {
  id: string;
  /** 店里的时区 —— 「今天/明天」按它算,不按这台电脑 */
  timezone: string;
  drivers: DriverRow[];
  zones: ZoneRow[];
  permissions: string[];
  /** 租户功能开关 —— 「特殊物品」关着时不显示那一栏 */
  features?: Record<string, boolean>;
  onClose: () => void;
  /**
   * 这一单的最新样子 —— 抽屉每次重新读到数据就报一次。
   *
   * ★ 让**列表当场跟着变**,而不是等人把抽屉关掉再整表重拉。
   *   用户报的就是这个:在详情里点完送达,身后那张列表一动不动,
   *   看起来像没生效。
   *   报的是整条数据、不是「我做了什么动作」—— 动作有十几种,
   *   一种一种往外报的话,漏掉一种就是一处不会变的界面。
   */
  onChanged?: (order: Detail) => void;
  /** 「再来一单」—— 把这一单的资料交给建单弹窗去预填 */
  onRepeat?: (order: Detail) => void;
}) {
  const [data, setData] = useState<Detail | null>(null);
  const [printing, setPrinting] = useState(false);
  const [changingPhone, setChangingPhone] = useState(false);
  const [notes, setNotes] = useState<NotificationRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const fresh = await api<Detail>(`/orders/${id}`);
      setData(fresh);
      onChanged?.(fresh);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    }
    // onChanged 每次渲染都是新函数;放进依赖会让这一屏无限重读
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * 通知记录单独拉一次 —— 不塞进订单详情。
   * 详情那个接口已经带着事件、凭证、明细,再多一张表的 50 行
   * 会让最常用的那个请求变重,而通知只有出问题时才有人看。
   */
  useEffect(() => {
    void api<NotificationRow[]>(`/orders/${id}/notifications`)
      .then(setNotes)
      .catch(() => setNotes([]));
  }, [id]);

  // Esc 关闭 —— 抽屉的基本预期,不做的话用户会一直去找 ✕
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const meta = data ? STATUS_META[data.status] : null;
  /** 货到付款 —— 收件那一块整块变红,司机照着它去敲门 */
  const isCod = Number(data?.codAmount ?? 0) > 0 || data?.codPending === true;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="订单详情"
        /* 手机上占满整屏 —— 抽屉留一条缝没有任何用,反而少了一列字的宽度 */
        className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200
                     dark:border-slate-700 px-6 py-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 font-mono">
                  {data?.orderNo ?? '加载中…'}
                </h2>
                {meta && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>
                    {meta.label}
                  </span>
                )}
              </div>
              {data && (
                <p className="mt-0.5 text-sm text-slate-500">
                  运单号 {data.trackingCode}
                  {data.externalRef && ` · 外部单号 ${data.externalRef}`}
                </p>
              )}
            </div>
            <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl shrink-0">
              <InlineIcon icon={X} />
            </button>
          </div>
        </header>

        {error && (
          <div
            className="m-6 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                       dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300 text-sm"
          >
            {error}
          </div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            {/*
              ★ 「再来一单」放在操作区**上面**、和状态操作分开。
                它不改这一单,它建的是**另一单** —— 混在「标记已送达」
                「取消订单」中间的话,点错的代价是凭空多一张单。
            */}
            <div className="flex gap-2">
              {onRepeat && permissions.includes('order:create') && (
                <button
                  onClick={() => onRepeat(data)}
                  className="flex-1 rounded-xl border border-brand-300 py-2.5 text-sm
                             text-brand-700 dark:border-brand-800 dark:text-brand-400"
                >
                  ↻ 再来一单 —— 照这一单的资料新建一张
                </button>
              )}
              {/*
                ★ 单张打印放在这儿,而不是只留列表上的批量入口:
                  补打一张(纸卡了、标签掉了)是常事,
                  而为了补打一张要回列表里把它勾出来,人就不会用这个功能了。
              */}
              <button
                onClick={() => setPrinting(true)}
                className="shrink-0 rounded-xl border border-slate-300 px-4 py-2.5 text-sm
                           text-slate-700 dark:border-slate-600 dark:text-slate-300"
              >
                <InlineIcon icon={Printer} /> 打印小票
              </button>
            </div>

            {changingPhone && (
              <ChangePhoneDialog
                orderId={data.id}
                current={data.receiverPhone}
                receiverName={data.receiverName}
                onClose={() => setChangingPhone(false)}
                onDone={load}
              />
            )}

            {printing && (
              <PrintDialog orders={[toReceipt(data)]} onClose={() => setPrinting(false)} />
            )}

            {/* 操作放最上面:打开详情多半就是为了处理它,不该滚到底才找到 */}
            <OrderActions
              order={data}
              drivers={drivers}
              zones={zones}
              permissions={permissions}
              features={features}
              onDone={load}
            />

            {/*
              ★ 这两件事要在**最上面**说,而且要显眼。
                「代收 $120」和「里面是冻品」都属于:错过了就
                当场造成损失,而事后补不回来(钱收不到、货化了)。
                摆在下面那些字段里,它们和「邮编」长得一样重。
            */}
            <SpecialNotice order={data} />

            {/*
              ★ 改代收 / 特殊物品的入口就放在提醒旁边。
                这两样是下单之后最常变的(客户少订一件、装货时才发现是冻品),
                而在这之前唯一的办法是取消重下 —— 单号变了、司机白跑一趟。
            */}
            <HandlingEditor
              order={data}
              canEdit={permissions.includes('order:update')}
              features={features}
              onSaved={load}
            />

            {data.needsReview && (
              <div
                className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200
                           dark:border-amber-900 px-4 py-3 text-sm text-amber-800 dark:text-amber-300"
              >
                ⚠ 该订单缺少坐标或区域存疑,上不了地图,需要人工复核
              </div>
            )}

            {/*
              ★ 货到付款的单,**收件人这一块整块变红**。
                司机是照着这块信息去敲门的,而「要不要收钱」和
                「收谁的钱」是同一件事 —— 分开摆的话,
                看完地址就出发的人不会再滚回去看金额那一行。
            */}
            <Section title="收件" danger={isCod}>
              <Field label="收件人" value={data.receiverName} danger={isCod} />
              <Field label="电话" value={data.receiverPhone} tel danger={isCod} />
              {/*
                ★ 「改电话」就放在电话旁边。
                  在这之前电话写错只能**取消整单重下** —— 单号变了、
                  司机白跑一趟,只为了改一个号码。
                  而这件事往往是司机在门口打不通时才发现的。
              */}
              {permissions.includes('order:update') && !isTerminal(data.status as OrderStatus) && (
                <div className="-mt-1 pl-[5.5rem] text-xs">
                  <button
                    onClick={() => setChangingPhone(true)}
                    className="text-brand-600 hover:underline dark:text-brand-400"
                  >
                    改电话
                  </button>
                </div>
              )}
              <Field label="地址" value={data.receiverAddress} danger={isCod} />
              {isCod && (
                <Field
                  label="货到付款"
                  value={`${money(data.codAmount, data.currency)} —— 送到时要向收件人收这笔钱`}
                  danger
                />
              )}
              {data.receiverPostcode && <Field label="邮编" value={data.receiverPostcode} />}
              {/*
                ★ 订单的备注**只有一个字段**(用户原话:「不管怎么操作
                  都是操作同一个属性」)。联系人的常驻备注在建单时就并了进去,
                  所以这里不再有第二条「常驻备注」。

                ★ 司机加的那条**排在它上面**,而且是另一个字段。
                  用户原话:「司机加的那个是单独的,主要是司机看,
                  放到正常备注的上一行来显示」。来源不同、读者不同 ——
                  合成一段之后,司机写的「门口有狗」和客服写的「放前台」
                  混在一起,出事时答不出这句是谁写的。

                ★ 客服在后台记的那些(来电、改派原因)不在这两条里,
                  它们只在**时间轴**上 —— 那是内部讨论,司机看不到。
              */}
              {data.driverNote && <Field label="司机备注" value={data.driverNote} />}
              {data.receiverNote && <Field label="备注" value={data.receiverNote} />}
              <Field
                label="区域"
                value={data.zone ? `${data.zone.code} ${data.zone.name}` : '未匹配'}
              />
            </Section>

            <Section title="取货">
              <Field label="取货点" value={data.pickupPoint?.name ?? '临时地址'} />
              <Field label="地址" value={data.pickupAddress} />
              {data.pickupContact && <Field label="联系人" value={data.pickupContact} />}
              {data.pickupPhone && <Field label="电话" value={data.pickupPhone} tel />}
              {data.customer && <Field label="商家" value={data.customer.name} />}
            </Section>

            <Section title="包裹">
              {/*
                ★ 期望送达日期要在详情里看得见。
                  它决定两件事:司机端今天列不列这一单、过了这天要不要提醒他 ——
                  客服接到「我的货怎么还没到」的电话时,第一句要答的就是它。
              */}
              {data.expectedDeliveryAt && (
                <Field
                  label="期望送达"
                  value={expectedLabel(data.expectedDeliveryAt, timezone, data.status).text}
                />
              )}
              <Field label="件数" value={String(data.packageCount)} />
              {data.itemDescription && <Field label="物品" value={data.itemDescription} />}
              {data.weightGram != null && <Field label="重量" value={`${data.weightGram} g`} />}
              {data.customerPrice != null && (
                <Field label="客户运费" value={money(data.customerPrice, data.currency)} />
              )}
              {data.driverPayout != null && (
                <div>
                  <Field label="司机报酬" value={money(data.driverPayout, data.currency)} />
                  {/*
                    ★ 改酬劳的入口就放在这个数旁边。
                      放在别处的话,看到「这单只给了 5 块」的人
                      还得先想起来去哪儿改。
                  */}
                  <PayoutEditor
                    orderId={data.id}
                    payout={String(data.driverPayout)}
                    currency={data.currency}
                    canEdit={permissions.includes('order:reprice')}
                    onSaved={load}
                  />
                </div>
              )}
              {data.codAmount !== '0' && (
                <Field label="代收货款" value={money(data.codAmount, data.currency)} />
              )}
              {/*
                ★ 明细摊开摆着,不折叠。
                  客户在电话那头问「为什么这单收 18 块」,客服要能一行一行念;
                  藏在一个「查看明细」后面,等于多一次点击 + 一次「在哪儿来着」。
              */}
              {data.priceBreakdown && data.priceBreakdown.lines.length > 0 && (
                <div className="col-span-full mt-1 rounded-md bg-slate-50 px-3 py-2 text-xs dark:bg-slate-800/60">
                  <div className="mb-1 text-slate-400">运费明细</div>
                  {data.priceBreakdown.lines.map((l, i) => (
                    <div key={`${l.code}-${i}`} className="flex justify-between py-0.5">
                      <span className="text-slate-500 dark:text-slate-400">{l.label}</span>
                      <span className="font-mono text-slate-700 dark:text-slate-200">
                        {money(String(l.amount), data.currency)}
                      </span>
                    </div>
                  ))}
                  <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-medium dark:border-slate-700">
                    <span>合计</span>
                    <span className="font-mono">
                      {money(data.customerPrice ?? '0', data.currency)}
                    </span>
                  </div>
                  {data.priceBreakdown.remoteToDriver > 0 && (
                    <div className="mt-1 text-slate-400">
                      偏远加价 {money(String(data.priceBreakdown.remoteToDriver), data.currency)}{' '}
                      已计入司机报酬
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title="配送">
              <Field label="司机" value={data.driver?.name ?? '未派单'} />
              {data.driver && <Field label="司机电话" value={data.driver.phone} tel />}
              {data.signedByType && (
                <Field label="签收方式" value={labelOf(SIGNED_BY_LABEL, data.signedByType)} />
              )}
              {data.signedByName && <Field label="签收人" value={data.signedByName} />}
              {data.cancelReason && <Field label="取消原因" value={data.cancelReason} />}
            </Section>

            {data.remark && (
              <Section title="内部备注">
                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {data.remark}
                </p>
              </Section>
            )}

            {data.proofs.length > 0 && (
              <Section title={`凭证(${data.proofs.length})`}>
                <ProofGallery proofs={data.proofs} labels={PROOF_LABEL} />
              </Section>
            )}

            <Section title="时间轴">
              <OrderTimeline events={data.events} />
            </Section>

            {/*
              ★ 「他到底收到通知没有」。
                客户投诉「司机没来」的时候,这是第一个要回答的问题;
                在这之前 notification_log 只写不读,记了一年谁也看不到。
                失败原因也要摆出来 —— 「通知过了」和「通知发出去了」
                是两件事。
            */}
            {notes.length > 0 && (
              <Section title={`通知(${notes.length})`}>
                <ul className="space-y-1.5 text-xs">
                  {notes.map((n, i) => (
                    <li key={i} className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-slate-700 dark:text-slate-300">
                          {CHANNEL_LABEL[n.channel] ?? n.channel}
                        </span>
                        <span className="ml-2 text-slate-400">
                          {new Date(n.createdAt).toLocaleString('zh-CN', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {n.skipReason && (
                          <div className="text-amber-600 dark:text-amber-400 break-all">
                            {n.skipReason}
                          </div>
                        )}
                      </div>
                      <span
                        className={`shrink-0 ${
                          n.status === 'SENT'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-slate-400'
                        }`}
                      >
                        {n.status === 'SENT' ? '已发出' : '未发出'}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function Section({
  title,
  danger,
  children,
}: {
  title: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3
        className={`mb-2 text-sm font-semibold ${
          danger ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'
        }`}
      >
        {title}
      </h3>
      <div
        className={`rounded-xl border p-4 space-y-2 ${
          danger
            ? 'border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30'
            : 'border-slate-200 dark:border-slate-700'
        }`}
      >
        {children}
      </div>
    </section>
  );
}

/*
 * ★ 值用 whitespace-pre-line 渲染:备注是**多行**的(常驻那句和
 *   这一单临时交代的那句各占一行)。不保留换行的话两句会挤成一行,
 *   而「大门密码 1234放前台」这种粘在一起的字,人得读两遍才分得开。
 */
function Field({
  label,
  value,
  tel,
  danger,
}: {
  label: string;
  value: string;
  tel?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex gap-3 text-sm">
      <span
        className={`w-20 shrink-0 ${
          danger ? 'text-red-500/80 dark:text-red-400/80' : 'text-slate-500 dark:text-slate-400'
        }`}
      >
        {label}
      </span>
      {tel ? (
        <a href={`tel:${value}`} className="text-brand-600 dark:text-brand-500 hover:underline">
          {value}
        </a>
      ) : (
        <span
          className={`break-words min-w-0 whitespace-pre-line ${
            danger
              ? 'font-medium text-red-700 dark:text-red-300'
              : 'text-slate-900 dark:text-slate-100'
          }`}
        >
          {value}
        </span>
      )}
    </div>
  );
}

/**
 * 顶上那条提醒 —— 货到付款 / 特殊物品。
 *
 * ★ 为什么不只在字段里显示:这两件事错过了是**当场的损失**
 *   (钱没收到、冻品化了),而字段区里它和「邮编」一样大。
 */
function SpecialNotice({
  order,
}: {
  order: {
    codAmount: string;
    codPending?: boolean;
    currency: string;
    specialItems?: SpecialItemDto[];
  };
}) {
  const cod = Number(order.codAmount) > 0 || order.codPending === true;
  const items = order.specialItems ?? [];
  if (!cod && items.length === 0) return null;

  return (
    <div aria-label="特别提醒" className="space-y-2">
      {cod && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium
                     text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {/* ★ 待定的单库里是 0 —— 念成「货到付款 $0.00」会被读成「不用收」 */}
          <InlineIcon icon={Banknote} /> 货到付款{' '}
          {order.codPending
            ? '金额待定 —— 司机端显示「联系商家确认」,送达时按实收金额录入'
            : `${money(order.codAmount, order.currency)} —— 司机送到时要收这笔钱`}
        </div>
      )}
      {items.length > 0 && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium
                     text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
        >
          ⚠ 特殊物品:
          {items.map((i) => `${i.icon ? `${i.icon} ` : ''}${i.label}`).join('、')}
        </div>
      )}
    </div>
  );
}

/** 金额是最小单位的整数字符串(分/仙),显示时才除 100 —— 全程不碰浮点 */
